import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useCreateMasterDownloadLinkMutation } from '../../../../../redux/api/fymAPI';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { masterPlayerMessages } from './masterPlayerMessages';

/** Opens a link in a way that makes the browser save the file and stay on the page. */
export function startBrowserDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

interface UseMasterDownloadProps {
  accountId: number;
  itemUuid: string;
}

/**
 * Downloads the original master (design release-kit o): `POST .../master/download` gives a
 * 5-minute link with `Content-Disposition: attachment` and the original name. It works while
 * the AAC copy is not ready and for a failed master.
 */
export function useMasterDownload({ accountId, itemUuid }: UseMasterDownloadProps) {
  const intl = useIntl();
  const { showError } = useShowToast();
  const [createLink, { isLoading }] = useCreateMasterDownloadLinkMutation();

  const download = useCallback(async () => {
    try {
      const { url } = await createLink({ accountId, itemUuid }).unwrap();
      startBrowserDownload(url);
    } catch {
      showError(intl.formatMessage(masterPlayerMessages.downloadFailed));
    }
  }, [accountId, createLink, intl, itemUuid, showError]);

  return { download, isLoading };
}
